import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Count of "active" direct loads for a shipper that warrant attention in nav:
 *   - live: status='posted' AND bid_deadline > now()
 *   - in-flight: status='awarded' AND pickup_date >= today
 * Realtime updates via postgres_changes on direct_loads filtered by created_by.
 */
export function useShipperMyLoadsCount(userId: string | null | undefined, enabled: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId || !enabled) {
      setCount(0);
      return;
    }
    let cancelled = false;

    const fetchCount = async () => {
      const nowIso = new Date().toISOString();
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const [{ count: liveC }, { count: awardC }] = await Promise.all([
        supabase
          .from("direct_loads" as any)
          .select("id", { count: "exact", head: true })
          .eq("created_by", userId)
          .is("deleted_at", null)
          .eq("status", "posted")
          .gt("bid_deadline", nowIso),
        supabase
          .from("direct_loads" as any)
          .select("id", { count: "exact", head: true })
          .eq("created_by", userId)
          .is("deleted_at", null)
          .eq("status", "awarded")
          .gte("pickup_date", todayStr),
      ]);

      if (!cancelled) setCount((liveC ?? 0) + (awardC ?? 0));
    };
    fetchCount();

    const channel = supabase
      .channel(`shipper-my-loads-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "direct_loads",
          filter: `created_by=eq.${userId}`,
        },
        () => fetchCount()
      )
      .subscribe((status) => { if (status === "SUBSCRIBED") fetchCount(); });

    // Periodic refresh so the badge reacts to bid_deadline / pickup_date passing.
    const interval = setInterval(fetchCount, 60_000);

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; fetchCount(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(interval);
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [userId, enabled]);


  return count;
}
